import React, { useState, useEffect, useMemo, useCallback, useId } from 'react';
import Downshift from 'downshift';
import { MdCancel } from "react-icons/md";
import { getLocationsData } from '../data/locations.js';

const SearchBar = ({ onSearch, setHoveredLocation, searchQuery }) => {
  const [inputValue, setInputValue] = useState(searchQuery || '');
  const [isOpen, setIsOpen] = useState(false);
  const [spotsData, setSpotsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Downshift generates ids like `downshift-5-input` using a global counter, which
  // differs between SSR and client hydration. Provide a deterministic id to avoid
  // hydration warnings.
  const reactId = useId();
  const downshiftId = useMemo(() => `downshift-${reactId.replace(/:/g, '')}`, [reactId]);

  // Load data from bundled locations
  useEffect(() => {
    const loadSpotsData = async () => {
      try {
        setIsLoading(true);
        // Use the bundled data instead of fetching
        const transformedData = await getLocationsData();
        setSpotsData(transformedData);
      } catch (error) {
        console.error('Error loading spots data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSpotsData();
  }, []);

  // Sync input value with external searchQuery prop
  useEffect(() => {
    setInputValue(searchQuery || '');
  }, [searchQuery]);

  // Memoize filtered options to avoid recalculating on every render
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim() || isLoading) return [];
    
    const filtered = spotsData
      .filter(item => 
        item.text?.toLowerCase().includes(inputValue.toLowerCase())
      );
    
    // Sort to prioritize surfspots first, then towns
    const sorted = filtered.sort((a, b) => {
      if (a.type === 'surfspot' && b.type !== 'surfspot') return -1;
      if (a.type !== 'surfspot' && b.type === 'surfspot') return 1;
      // If both are the same type, sort alphabetically
      return a.text.localeCompare(b.text);
    });
    
    return sorted.slice(0, 50);
  }, [spotsData, inputValue, isLoading]);

  const handleSelection = useCallback((selectedItem) => {
    if (selectedItem) {
      onSearch(selectedItem);
      setInputValue(''); // Clear input to close dropdown
      setIsOpen(false); // Force close dropdown
    }
  }, [onSearch]);

  const handleClearSearch = useCallback(() => {
    setInputValue('');
    setIsOpen(false);
    onSearch?.(null);
    setHoveredLocation?.(null);
  }, [onSearch, setHoveredLocation]);

  const handleMouseEnter = useCallback((item) => {
    setHoveredLocation?.(item);
  }, [setHoveredLocation]);

  const handleMouseLeave = useCallback(() => {
    setHoveredLocation?.(null);
  }, [setHoveredLocation]);

  // Improved text highlighting with better regex escaping
  const highlightText = useCallback((text, query) => {
    if (!query?.trim() || !text) return text;
    
    // Escape special regex characters
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="bg-yellow-300 dark:bg-yellow-700">
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  }, []);

  return (
    <Downshift
      id={downshiftId}
      itemToString={() => ''} // Always return empty string to keep input clear
      onChange={handleSelection}
      inputValue={inputValue}
      onInputValueChange={(value) => {
        setInputValue(value);
        setIsOpen(value.trim().length > 0 && filteredOptions.length > 0);
      }}
      isOpen={isOpen}
      onStateChange={(changes) => {
        if (changes.type === Downshift.stateChangeTypes.blurInput) {
          setIsOpen(false);
        }
      }}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        clearSelection,
        closeMenu,
      }) => {
        const showDropdown = isOpen && inputValue.trim().length > 0 && filteredOptions.length > 0;

        return (
          <div className="relative w-full">
            <div className="relative">
              <input
                {...getInputProps({
                  placeholder: isLoading ? 'Loading locations...' : 'Search location',
                  disabled: isLoading,
                  className: `w-full px-4 py-2 pr-12 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 text-black dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`,
                  'aria-label': 'Search for locations',
                  onFocus: () => setIsOpen(inputValue.trim().length > 0 && filteredOptions.length > 0),
                })}
              />
              {inputValue.length > 0 && !isLoading && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                  onClick={() => {
                    clearSelection();
                    handleClearSearch();
                  }}
                  aria-label="Clear search"
                >
                  <MdCancel size={20} />
                </button>
              )}
            </div>
            
            {showDropdown && (
              <ul
                {...getMenuProps()}
                className="absolute w-full max-h-60 overflow-y-auto mt-1 rounded shadow-lg z-[9999] border border-zinc-300 dark:border-zinc-600 bg-white/90 dark:bg-zinc-700/90 backdrop-blur-sm"
                role="listbox"
              >
                {filteredOptions.map((item, index) => (
                  <li
                    key={`${item.type}-${item.id}`}
                    {...getItemProps({
                      index,
                      item,
                      className: `px-4 py-2 cursor-pointer border-b border-zinc-200/50 dark:border-zinc-600/50 last:border-b-0 ${
                        highlightedIndex === index 
                          ? 'bg-blue-100/80 dark:bg-blue-600/80 text-black dark:text-white' 
                          : 'hover:bg-blue-50/60 dark:hover:bg-blue-800/60 text-black dark:text-white'
                      } transition-colors`,
                      onMouseEnter: () => handleMouseEnter(item),
                      onMouseLeave: handleMouseLeave,
                    })}
                    role="option"
                    aria-selected={highlightedIndex === index}
                  >
                    <div className="flex justify-between items-center">
                      <span>{highlightText(item.text, inputValue)}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-2">
                        {item.type}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

export default SearchBar;
